import { PropsWithChildren, useCallback, useEffect } from 'react';
import cx from 'classnames';
import chevronDown from 'assets/icons/arrow_light_down.svg';
import { useToggle } from 'customHooks/useToggle';
import Button, { Color } from 'lib/Button';
import SortButton from 'lib/SortButton';
import IncludeExcludeToggle from 'lib/IncludeExcludeToggle/IncludeExcludeToggle';
import { CodeNameModel } from 'components/common/types';
import { SortDir } from 'components/common/types/SortDir.types';
import { CollapsibleListProps } from './CollapsibleList.types';
import { useResizeHeight } from './useResizeHeight';

const CollapsibleList: React.FC<PropsWithChildren<CollapsibleListProps>> = ({
  label,
  children,
  headerComponent,
  onSort,
  onClearAll,
  onToggle,
  isSortVisible,
  isClearAllVisible,
  icon = chevronDown,
  sortDir = SortDir.NONE,
  isOpen = false,
  category,
  customExpandedHeight,
  groupIncludeState,
  onGroupInclude,
  onGroupExclude,
  hideGroupExclude,
  isGroupDeselectAllowed,
}) => {
  const { isOpen: isToggledOpen, toggle } = useToggle(isOpen);
  const { ref, containerHeight } = useResizeHeight(isToggledOpen);

  useEffect(() => {
    if (isOpen !== isToggledOpen) {
      toggle();
    }
  }, [isOpen]);

  const handleToggle = (): void => {
    toggle();
    onToggle?.(category);
  };

  const handleGroupToggle = useCallback(
    (updated: CodeNameModel) => {
      if (updated.include === undefined) onClearAll?.(category);
      else if (updated.include) onGroupInclude?.(category);
      else onGroupExclude?.(category);
    },
    [category, onClearAll, onGroupInclude, onGroupExclude],
  );

  return (
    <>
      <div className="flex justify-between w-full items-center">
        <button
          type="button"
          data-test-id="collapsible-list-label"
          className={cx('sub-header-base truncate cursor-pointer text-left bg-transparent border-0 p-0', {
            'text-neutral-900': !isToggledOpen,
            'text-primary-700': isToggledOpen,
          })}
          onClick={handleToggle}
        >
          {label}
        </button>
        {headerComponent}
        {onGroupInclude && (
          <IncludeExcludeToggle
            option={{
              code: String(label),
              name: String(label),
              include: groupIncludeState,
            }}
            isAlwaysVisible
            isHovered={false}
            isSelected={groupIncludeState !== undefined}
            isOptionNameSelectable={false}
            hideExclude={hideGroupExclude}
            onIncludeExcludeChange={handleGroupToggle}
            isDeselectAllowed={isGroupDeselectAllowed}
          />
        )}
        <div>
          <Button
            onClick={handleToggle}
            dataTestId="collapsible-list-header-button"
            svg={icon}
            color={Color.TRANSPARENT}
            classNames={cx('hover:bg-transparent active:bg-transparent')}
            svgClassnames={cx('transition-all duration-300', {
              'text-neutral-900 rotate-0': !isToggledOpen,
              'text-primary-600 rotate-180': isToggledOpen,
            })}
          />
        </div>
      </div>
      <div
        className={cx('transition-all duration-300 w-full overflow-y-hidden', {
          'mt-2': isToggledOpen,
        })}
        data-test-id={`collapse-container-${isToggledOpen ? 'expanded' : 'collapsed'}`}
        style={{ maxHeight: isToggledOpen ? `${customExpandedHeight ?? containerHeight}px` : 0 }}
      >
        <div ref={ref}>
          <div className={cx('flex justify-between', { 'min-h-[32px] items-center': isSortVisible })}>
            {isSortVisible ? <SortButton sortDir={sortDir} onSort={onSort} dataTestId="collapsible-list-sort" /> : null}
            {isClearAllVisible ? (
              <Button color={Color.TRANSPARENT} label="Clear all" onClick={() => onClearAll?.(category)} />
            ) : null}
          </div>
          {children}
        </div>
      </div>
    </>
  );
};

export default CollapsibleList;
